import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Box,
  Tooltip,
  Divider,
  CircularProgress,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import { getDbHeader, getHeader } from "../../utils/util";
import { toast } from "react-toastify";
import BackBtn from "../../widgets/BackBtn";

function MetaPixelConfigForm() {
  const [pixelId, setPixelId] = useState("");
  const [trackPageView, setTrackPageView] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API_ENDPOINT}/event/get-meta-pixel-config`, {
      ...getDbHeader(),
      method: "GET",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.metaPixel) {
          setPixelId(data.metaPixel.pixelId || "");
          setTrackPageView(data.metaPixel.trackPageView ?? true);
        }
      })
      .catch(() => toast.error("Failed to load Meta Pixel configuration."));
  }, []);

  const validate = () => {
    if (!pixelId) {
      toast.error("Please provide a Meta Pixel ID.");
      return false;
    }
    if (!/^\d+$/.test(pixelId)) {
      toast.error(
        "Invalid Meta Pixel ID. It must be a numeric value (e.g., 1234567890)."
      );
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    fetch(`${API_ENDPOINT}/event/save-meta-pixel-config`, {
      method: "POST",
      ...getHeader(ADMIN),
      body: JSON.stringify({ pixelId, trackPageView }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => Promise.reject(d));
        return res.json();
      })
      .then((data) => toast.success(data.message))
      .catch((err) =>
        toast.error(err?.error || "Network error. Please try again.")
      )
      .finally(() => setLoading(false));
  };

  return (
    <Container sx={{ py: 4 }}>
      <BackBtn />
      <Paper elevation={3} sx={{ p: 4, maxWidth: 700, margin: "auto" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Configure Meta Pixel
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Connect your Facebook / Meta Pixel to track conversions, build
          audiences, and measure ad performance — without touching{" "}
          <code>index.html</code>.
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Meta Pixel ID"
            value={pixelId}
            onChange={(e) => setPixelId(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="e.g., 1422494059559322"
            helperText="Numeric ID found in Meta Events Manager → your Pixel → Settings."
            InputProps={{
              endAdornment: (
                <Tooltip
                  title="Found in Meta Business Suite → Events Manager → Data Sources → your Pixel → Settings. It's a long numeric string like 1422494059559322."
                  arrow
                >
                  <InfoIcon color="action" sx={{ cursor: "pointer" }} />
                </Tooltip>
              ),
            }}
          />

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={trackPageView}
                  onChange={(e) => setTrackPageView(e.target.checked)}
                />
              }
              label='Track PageView on Load (fbq "track", "PageView")'
            />
            <Tooltip
              title='When on, fbq("track", "PageView") fires immediately after the pixel initialises. Turn off if you want to fire PageView manually, e.g. after consent or on route changes.'
              arrow
            >
              <InfoIcon color="action" sx={{ ml: 0.5, cursor: "pointer" }} />
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ ml: 7, mb: 2 }}
          >
            {trackPageView
              ? "A PageView event will fire automatically when the pixel loads."
              : "You must fire PageView manually via fbq('track', 'PageView') in your code."}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" gutterBottom>
            What Meta Pixel Tracks
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Meta Pixel lets you track <strong>standard events</strong>{" "}
            (PageView, Purchase, AddToCart, Lead, etc.) and{" "}
            <strong>custom events</strong> to measure your Facebook and
            Instagram ad performance. Once installed, you can create retargeting
            audiences, run conversion campaigns, and view attribution data — all
            from Meta Ads Manager. The <strong>noscript fallback</strong> is
            also injected automatically for browsers with JavaScript disabled.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ mt: 1 }}
          >
            {loading ? "Saving…" : "Save Configuration"}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default MetaPixelConfigForm;
